import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { LibraryChangesList } from "./LibraryChangesList.js";
import { RagBadge } from "./RagBadge.js";
import { RAG_DOCUMENT_STATUS_LABELS } from "./RagDocumentActions.js";
import type { RagDocument } from "../types.js";

const TYPE_LABELS: Record<string, string> = {
  PDF: "PDF document",
  DOCX: "Microsoft Word document",
  PPTX: "Microsoft PowerPoint presentation",
  XLSX: "Microsoft Excel spreadsheet",
  CSV: "CSV spreadsheet",
  HTML: "HTML document",
  MD: "Markdown document",
};

function documentTypeLabel(type: string): string {
  const key = type.toUpperCase();
  return TYPE_LABELS[key] ?? `${key} document`;
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium text-[var(--lm-muted)] dark:text-slate-400">{label}</dt>
      <dd className="break-words text-sm text-[var(--lm-foreground-strong)] dark:text-slate-200">{children}</dd>
    </div>
  );
}

export interface RagDocumentDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: RagDocument | null;
  workspaceName: string;
  libraryName: string;
  onView?: (documentId: string) => void;
  onViewChange?: (documentId: string, title: string, documentVersionId?: string | null) => void;
  onStatus?: (documentId: string) => void;
  libraryId?: string;
}

export function RagDocumentDetailsSheet({
  open,
  onOpenChange,
  document: doc,
  workspaceName,
  libraryName,
  onView,
  onViewChange,
  onStatus,
  libraryId,
}: RagDocumentDetailsSheetProps) {
  const canOpen = Boolean(doc && onView && (doc.status === "completed" || doc.status === "failed"));
  const canViewStatus = Boolean(
    doc && onStatus && (doc.status === "processing" || doc.status === "pending"),
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        overlayClassName="bg-[var(--overlay)] backdrop-blur-[1px] dark:bg-slate-950/65"
        className={cn(
          "flex h-full flex-col gap-0 p-0 sm:max-w-[380px]",
          "border-[var(--lm-border-subtle)] shadow-[-12px_0_40px_rgba(16,24,40,0.12)]",
          "dark:border-white/10 dark:bg-[#0b1120] dark:shadow-[-12px_0_40px_rgba(0,0,0,0.55)]"
        )}
      >
        <SheetHeader className="border-[var(--lm-border-subtle)] dark:border-white/10">
          <SheetTitle>Info</SheetTitle>
          {doc ? (
            <SheetDescription className="truncate text-[11px] text-[var(--lm-muted)] dark:text-slate-400">
              {doc.size} · {doc.title}
            </SheetDescription>
          ) : null}
        </SheetHeader>

        {doc ? (
          <>
            <SheetBody className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <dl className="shrink-0 space-y-4 rounded-xl border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] p-4 dark:border-white/10 dark:bg-slate-950/50">
                <InfoRow label="Name">{doc.title}</InfoRow>
                <InfoRow label="Type">{documentTypeLabel(doc.type)}</InfoRow>
                <InfoRow label="Location">
                  <span>
                    {workspaceName}
                    <span className="text-[var(--lm-muted)] dark:text-slate-500"> / </span>
                    <span className="text-violet-600 dark:text-violet-300">{libraryName}</span>
                  </span>
                </InfoRow>
                <InfoRow label="Total size">{doc.size}</InfoRow>
                <InfoRow label="Date added">{doc.uploadedAt || doc.updatedAt}</InfoRow>
                <InfoRow label="Status">
                  <RagBadge variant={doc.status}>{RAG_DOCUMENT_STATUS_LABELS[doc.status]}</RagBadge>
                </InfoRow>
              </dl>
              {libraryId ? (
                <LibraryChangesList
                  className="mt-4 min-h-0 flex-1"
                  libraryId={libraryId}
                  documentId={doc.id}
                  documentTitle={doc.title}
                  onView={(documentId, title, documentVersionId) => {
                    if (onViewChange) {
                      onViewChange(documentId, title, documentVersionId);
                    } else {
                      onView?.(documentId);
                    }
                    onOpenChange(false);
                  }}
                />
              ) : null}
            </SheetBody>

            {canOpen || canViewStatus ? (
              <SheetFooter className="border-[var(--lm-border-subtle)] dark:border-white/10 sm:justify-stretch">
                {canOpen ? (
                  <Button
                    type="button"
                    variant="accent"
                    className="w-full sm:w-full"
                    onClick={() => {
                      onView?.(doc.id);
                      onOpenChange(false);
                    }}
                  >
                    Open
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:w-full"
                    onClick={() => {
                      onStatus?.(doc.id);
                      onOpenChange(false);
                    }}
                  >
                    View status
                  </Button>
                )}
              </SheetFooter>
            ) : null}
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
